import { FileActionButton } from "../file-action/file-action-controls";
import { useTranslation } from "react-i18next";
import ViewAllProductsDataTable from "./view-all-products-datatable";
import { Suspense, useEffect, useRef, useState } from "react";
import AppLoading from "../app-loading/app-loading";
import { Toast } from "primereact/toast";
import ProductSearch from "./product-search";
import ProductService from "../../services/products/products.service";
import { OtherUserModel } from "../../models/all-user/edit-user-model";
import { ProductExport } from "./product-list-export";
import { ProductPriceExport } from "./product-price-export";

const ViewAllProducts = () => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const debounceTimer = useRef<any>(null);
  const [refreshPage, setRefreshPage] = useState(true);
  const [filteredProducts, setFilteredProducts] = useState<OtherUserModel[]>([]);
  const [productList, setProductList] = useState<OtherUserModel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleSearch("");
  }, [refreshPage]);

 const getAllProducts = () => {
  setLoading(true);
  
  // Refresh the shipping products cache when the page loads/refreshes
  ProductService.getAllProductsForShipping(true);

  ProductService.getAllAdminProducts()
    .then((response) => {
      if (response && response.isSuccess === false) {
        toast.current?.show({
          severity: 'error',
          summary: 'Access Denied',
          detail: response.message,
          life: 3000
        });
        setProductList([]);
        setFilteredProducts([]);
      } else {
        setProductList(response || []);
        setFilteredProducts(response || []);
      }
    })
    .catch((error) => {
      console.error("Error fetching products:", error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
        life: 3000
      });
      setProductList([]);
      setFilteredProducts([]);
    })
    .finally(() => {
      setLoading(false);
    });
};

  useEffect(() => {
    getAllProducts();
  }, []);

  function handleSearch(query: string): void {
    setLoading(true);
    if (productList && productList.length > 0) {
      const searchedProducts = productList.filter(
        (product) =>
          product.name &&
          typeof product.name === "string" &&
          product.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProducts(searchedProducts);
    } else {
      setFilteredProducts([]);
    }
    setTimeout(() => setLoading(false), 200);
  }

  const reloadPage = (val: boolean) => {
    setRefreshPage(val);
  };

  const handleEditProductSuccess = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
        ProductService.clearProductCache();
        getAllProducts();
        setRefreshPage(prev => !prev);
    }, 1000);
  };

  // ✅ 🔥 Export Product Price with Latest Data

  const handleProductPriceExport = async () => {
    try {
      setLoading(true);
      const response = await ProductService.getAllAdminProducts(); // ✅ Get latest data
      ProductPriceExport(response); // ✅ Pass to export function
      setLoading(false);
    } catch (error) {
      console.error('Error exporting product price:', error);
      setLoading(false);
    }
  };

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} position="top-right" />
        <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
          <div className="col-12">
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
              <h5 className="ml-1 mb-0">{t("view_all_products")}</h5>

              <div className="flex gap-2">
                {/* ✅ Export Product List based on current view */}
                <FileActionButton action="export"
                  className="text-black border-round"
                  onClick={() => ProductExport(productList)}
                  icon="pi pi-file-export"
                  text
                  raised
                >
                  Export Product List
                </FileActionButton>

                {/* ✅ Export Product Price with latest data */}
                <FileActionButton action="export"
                  className="text-black border-round"
                  onClick={handleProductPriceExport}
                  icon="pi pi-file-pdf"
                  text
                  raised
                >
                  Export Product Price
                </FileActionButton>
              </div>

              <ProductSearch
                onSearch={handleSearch}
                reloadPage={reloadPage}
                setLoading={setLoading}
              />
            </div>
          </div>

          <ViewAllProductsDataTable
            data={filteredProducts}
            handleEditProductSuccess={handleEditProductSuccess}
          />
        </div>
        {loading && <AppLoading />}
      </Suspense>
    </>
  );
};

export default ViewAllProducts;
